<template>
  <div>
    <div class="container">
      <form
        id="pricing-form"
        ref="form"
        name="form"
        :action="urlPrefix + '/pricing-form-submit'"
        method="POST"
        v-on:submit.prevent
      >
        <div class="flex center-xs space-top-1 md-space-top-2 space-bottom-1">
          <h2 class="formstep-title maison-typo">{{ title }}</h2>
        </div>

        <ProgressBar
          v-if="showProgressBar"
          :currentStep="currentStep"
          :totalSteps="currentSteps.length"
          :custom-breakpoints="customBreakpoints"
          :breakpoints="breakpoints"
        />

        <Component
          v-if="currentStepData"
          :is="getComponent"
          :step="currentStepData"
          :order="stepOrder"
          v-on:progress="progress"
          v-on:back="back()"
        />

        <HiddenFieldset v-if="formFields"></HiddenFieldset>
      </form>
    </div>
    <div
      v-html="richText(legalText)"
      class="center-xs start-s space-top-3 legal-container legal-text"
    ></div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { createClient } from "contentful";

import Checkbox from "../components/Checkbox.vue";
import Dropdown from "../components/Dropdown";
import Email from "../components/Email";
import FirstnameLastname from "../components/FirstnameLastname";
import HiddenFieldset from "../components/HiddenFieldset";
import Information from "../components/Information";
import Phone from "../components/Phone";
import ProgressBar from "../components/ProgressBar";
import Radio from "./Radio.vue";
import TextField from "../components/TextField";
import AnalyticsMixin from "../mixins/AnalyticsMixin";
import {
  LANG_CODE_TO_COUNTRY,
  WRITTEN_LANGUAGES,
  URL_TO_LOCALE_MAPPINGS,
} from "../../corpweb_globals";
import {
  analyticsAlias,
  analyticsIdentify,
  analyticsTrack,
  getHightouchId,
  isMobile,
} from "../../bundled_js/analytics_wrapper";

export default {
  mixins: [AnalyticsMixin],
  components: {
    Checkbox,
    Dropdown,
    Email,
    FirstnameLastname,
    HiddenFieldset,
    Information,
    Phone,
    ProgressBar,
    Radio,
    TextField,
  },
  data() {
    return {
      currentStep: 0,
      formSteps: [],
      showProgressBar: false,
      legalText: "",
      slug: "pricing",
      flow: "",
      customBreakpoints: false,
      breakpoints: [],
    };
  },
  computed: {
    getSize() {
      let size = '';
        if (this.formFields && this.formFields.assetcount && this.formFields.assetcount !== '') {
          size = this.formFields.assetcount;
        }
        if (this.formFields && this.formFields.equipmentsegment && this.formFields.equipmentsegment !== '') {
          size = this.formFields.equipmentsegment;
        }
        return size;
    },
    getFleetTypes() {
      return this.formFields && this.formFields.assets &&
      this.formFields.assets.length > 0 
        ? this.formFields.assets.toString()
        : '';
    },
    getIndustryValues() {
      return this.formFields && this.formFields.industry && this.formFields.industry.length > 0
          ? this.$store.state.formFields.industry.toString()
          : '';
    },
    getFeatureValues() {
      return this.formFields && this.formFields.features &&
      this.formFields.features.length > 0
        ? this.formFields.features.toString()
        : '';
    },
    getCurrentStepTitle() {
       return this.currentSteps[this.currentStep - 1]?.title || '';
    },
    getComponent() {
      const components = {
        checkbox: Checkbox,
        dropdown: Dropdown,
        email: Email,
        firstnameLastname: FirstnameLastname,
        information: Information,
        phone: Phone,
        radio: Radio,
        text: TextField,
      };
      return components[this.currentStepData.type];
    },
    currentSteps() {
      if (!this.doFormFieldsExist()) {
        return this.formSteps;
      }
      const businessUnit = this.formFields["businessunit"];
      if (businessUnit.length === 1 && businessUnit[0] === "Equipment") {
        return this.currentStepDataExperiment("D");
      }
      return this.currentStepDataExperiment("B");
    },
    currentStepData() {
      return this.currentSteps[this.currentStep];
    },
    title() {
      if (this.currentSteps.length > 0) {
        return this.currentStepData.title;
      } else {
        return "";
      }
    },
    lastStepIndex() {
      return this.currentSteps.length - 1;
    },
    maxSteps() {
        return this.currentSteps.length;
    },
    stepOrder() {
      if (this.currentStep === 0) {
        return "first";
      } else if (this.currentStep === this.lastStepIndex) {
        return "last";
      } else {
        return "middle";
      }
    },
    hasNoState() {
      return typeof this.$store.state === "undefined";
    },
    ...mapState([
      "formFields",
      "locale",
      "lastUpdated",
      "urlPrefix",
      "formTitle",
      "ipFetched",
      "ipAddress",
      "isEU",
      "functionalOptIn",
      "analyticsOptIn",
      "advertisingOptIn",
      "referralID",
      "submitted",
    ]),
  },
  watch: {
    currentStep: async function (currentStep) {
      // Retry logic for geolocation and gdpr
      if (!this.ipFetched) {
        this.geoLocate();
      }
      // Run advertising and analytics on last step
      // This ensures that variables such as mixpanel
      // and optimizely are defined
      if (currentStep === this.lastStepIndex) {
        if (this.advertisingOptIn) {
          this.runAdvertising();
        }
        if (this.analyticsOptIn) {
          this.runAnalytics();
        }
      }
    },
  },
  async created() {
    const urlPath = window.location.pathname.split("/");
    const langCode = urlPath[1];
    this.slug = urlPath[urlPath.length - 1];

    if (this.slug === "prueba-gratis") this.slug = "pricing";

    let hasLangCode = false;

    const existingLocale = this.locale;

    if (langCode in URL_TO_LOCALE_MAPPINGS) {
      this.$store.commit("urlPrefix", "/" + langCode);
      this.$store.commit("locale", URL_TO_LOCALE_MAPPINGS[langCode]);
      hasLangCode = true;
    } else {
      this.$store.commit("urlPrefix", "");
      this.$store.commit("locale", "en-US");
    }

    let parameters = {
      "fields.slug": this.slug,
    };

    // Logic to grab the urlprefix from the url. As we work on the form
    // consolidation project in Q122, this logic should be removed.
    if (
      (hasLangCode && urlPath.length > 3) ||
      (!hasLangCode && urlPath.length > 2)
    ) {
      parameters["fields.urlPrefix"] = urlPath[urlPath.length - 2];
    } else {
      parameters["fields.urlPrefix"] = "none";
    }

    const form = await this.getForm("form", this.locale, parameters);
    const fields = form.fields;
    const formTitleUpdated = fields.pageTitle !== this.formTitle;
    const localeUpdated = existingLocale !== this.locale;
    const formUpdated = form.lastUpdated != this.lastUpdated;

    this.formSteps = this.formatFormSteps(fields.questions);

    if (
      this.shouldRefreshFields(formTitleUpdated, localeUpdated, formUpdated)
    ) {
      const formFields = this.initializeFormFields(this.formSteps);
      this.$store.commit("setupFormFields", formFields);
      this.$store.commit("submitted", false);
    }

    this.$store.commit("lastUpdated", form.lastUpdated);
    this.$store.commit("formTitle", fields.pageTitle);
    this.$store.commit("zap", fields.zap);
    this.$store.commit("backButtonText", fields.backButtonText);
    this.$store.commit("thankYou", fields.thankYouRedirect);
    this.showProgressBar = fields.progressBar;
    this.customBreakpoints = fields.progressBarCustomBreakpoint || false;
    if (this.showProgressBar && this.customBreakpoints) {
      // we should validate the breakpoints to make sure the pricing form does not break

      if (
        this.validateBreakpoints(fields?.breakpointsForProgressBar?.breakpoints)
      ) {
        // this only needs to happen if progress bar is turned to true and we want custom break points instead of the default currentPage/totalPage
        this.breakpoints = fields.breakpointsForProgressBar.breakpoints;
      } else {
        // breakpoints did not pass validation so revert to default breakpoints
        this.customBreakpoints = false;
        this.breakpoints = [];
      }
    }
    this.legalText = fields.legalText;

    this.$store.commit(
      "country",
      LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()]
    );
    this.$store.commit(
      "writtenLanguage",
      WRITTEN_LANGUAGES[this.locale.toLowerCase()]
    );

    this.trackFunnelStepRetry(
       'paid pricing',
        window.location.pathname,
        LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()],
        0,
        null,
        this.getCurrentStepTitle,
        this.lastStepIndex,
        this.ipAddress,
        this.flow,
        this.formTitle,
        this.getSize,
        null,
        this.getFleetTypes, // fleet_type only for paid pricing form
        this.getIndustryValues,
        this.getFeatureValues
    );
  },
  methods: {
    getStepName(name) {
      let dict = {
        assetcount: "size",
        assets: "asset_type",
        features: "features",
        industry: "industry",
        firstnamelastname: "first & last name",
        email: "email",
        company: "company",
        phone: "phone",
      };
      return dict[name];
    },
    validateBreakpoints(breakpointsArr) {
      // breakpoints should be an array
      if (Array.isArray(breakpointsArr)) {
        // length of array has to match number of steps there are
        if (breakpointsArr.length === this.currentSteps.length) {
          for (let i = 0; i < breakpointsArr.length; i++) {
            // if a valid number is not given as one of the breakpoints - breakpoints are defined like : ["8", "20"]
            if (isNaN(Number(breakpointsArr[i]))) {
              return false;
            }
          }
          // all checks have cleared so we return true
          return true;
        }
        return false;
      }
      return false;
    },
    richText(nodes) {
      return documentToHtmlString(nodes);
    },
    formatFormSteps(formSteps) {
      return formSteps.map((step) => step.fields);
    },
    shouldRefreshFields(formTitleUpdated, localeUpdated, formUpdated) {
      if (
        typeof this.formFields === "undefined" ||
        this.submitted ||
        formTitleUpdated ||
        localeUpdated ||
        formUpdated
      ) {
        return true;
      } else {
        return false;
      }
    },
    next(inputData) {
      this.currentStep++;
      this.trackFunnelStep(
        "paid pricing",
        window.location.pathname,
        LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()],
        this.currentStep,
        this.getStepName(inputData),
        this.getCurrentStepTitle,
        this.maxSteps,
        this.ipAddress,
        this.flow,
        this.formTitle,
        this.getSize,
        null,
        this.getFleetTypes,  // fleet_type only for paid pricing form
        this.getIndustryValues,
        this.getFeatureValues
      );
    },
    back() {
      this.currentStep--;
    },
    setGDPROptInPreferences() {
      if (this.isEU === true) {
        const preferences = this.getGDPRPreferences();
        this.$store.commit("functionalOptIn", preferences.functionalOptIn);
        this.$store.commit("analyticsOptIn", preferences.analyticsOptIn);
        this.$store.commit("advertisingOptIn", preferences.advertisingOptIn);
      }
    },
    async geoLocate() {
      const ipData = await this.getIPData();
      if (ipData) {
        if (!ipData["is_eu"]) {
          this.$store.commit("ipAddress", ipData["ip"]);
          if (
            ipData["country_name"] === "United States" ||
            ipData["country_name"] === "United States of America" ||
            ipData["country_name"] === "Canada"
          ) {
            this.$store.commit("ipData", JSON.stringify(ipData));
          }
        } else {
          this.$store.commit("isEU", true);
          this.setGDPROptInPreferences();
        }

        this.$store.commit("ipFetched", true);
      }
    },
    setReferralID() {
      const urlParams = new URLSearchParams(window.location.search);
      const referralID = urlParams.get("referralID", "");
      if (referralID) {
        this.$store.commit("referralID", referralID);
      }
    },
    runAdvertising() {
      const cookies = this.getUTMCookies();
      cookies.forEach((cookie) => {
        this.$store.commit(cookie["key"], cookie["value"]);
      }, this);

      if (this.referralID) {
        // Set static campaign id for referrals if
        // referral id present in query parameters
        // Overwrite any old cid cookie value
        const REFERRAL_CAMPAIGN_ID = "7014p000001LGQD";
        this.$store.commit("cid", REFERRAL_CAMPAIGN_ID);
      }

      const quantcastID = this.getQuantcastID();
      this.$store.commit("quantcastID", quantcastID);
    },
    runAnalytics() {
      const mixpanelID = getHightouchId();
      const segmentID = getHightouchId();
      const experimentName = this.getOptimizelyExperimentName();

      this.$store.commit("mixpanelID", mixpanelID ? mixpanelID : "");
      this.$store.commit("segmentID", segmentID ? segmentID : "");
      this.$store.commit(
        "experimentName",
        experimentName ? experimentName : ""
      );
    },
    progress(value) {
      if (this.currentStep === this.lastStepIndex) {
        this.submitForm(value);
      } else {
        this.next(value);
      }
    },
    async recaptcha() {
      await this.$recaptchaLoaded();
      const token = await this.$recaptcha("login");
      this.$store.commit("recaptchaToken", token);
    },
    async submitForm(name) {
      this.trackFunnelStep(
          'paid pricing',
          window.location.pathname,
          LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()],
          this.currentStep + 1,
          this.getStepName(name),
          this.getCurrentStepTitle,
          this.maxSteps,
          this.ipAddress,
          this.flow,
          this.formTitle,
          this.getSize,
          null,
          this.getFleetTypes, // fleet_type only for paid pricing form
          this.getIndustryValues,
          this.getFeatureValues
      );
      await this.recaptcha();
      this.identify();

      this.$store.commit("submitted", true); // Makes sure we clear data

      // Calendar Experiment Starts Here
      await this.startCalendarExperiment();

      this.$nextTick(function () {
        this.$refs.form.submit();
      });
    },
    initializeFormFields(formSteps) {
      const formFields = {};
      formSteps.forEach((step) => {
        if (step.type === "checkbox") {
          formFields[step.name] = [];
        } else if (step.type === "informational") {
          // Informational type, don't add to formFields object
        } else if (step.type === "firstnameLastname") {
          formFields[step.name] = "";
          formFields[step.name2] = "";
        } else {
          formFields[step.name] = "";
        }
      }, this);
      return formFields;
    },
    async getForm(content_type, locale, options = {}) {
      const client = createClient({
        space: process.env.CONTENTFUL_SPACE_ID,
        accessToken: process.env.CONTENTFUL_ACCESS_TOKEN,
        host: process.env.CONTENTFUL_HOST,
        timeout: 30000,
        retryLimit: 10,
      });

      let initial = {
        content_type: content_type,
        locale: locale,
      };
      let query = {
        ...initial,
        ...options,
      };

      return await client.getEntries(query).then((result) => {
        if (result.items.length === 0) {
          console.error(
            "Contentful query for pricing form does not return any items for query: ",
            initial,
            options
          );
        } else {
          if (result.items[0]?.fields?.questions) {
            return {
              fields: result.items[0].fields,
              lastUpdated: result.items[0].sys.updatedAt,
            };
          } else {
            for (let i = 1; i < result.items; i++) {
              if (result.items[i]?.fields?.questions) {
                console.error(
                  `Multiple items returned from contentful pricing form query, index ${i} returned object with questions array for query: `,
                  initial,
                  options
                );
                return {
                  fields: result.items[i].fields,
                  lastUpdated: result.items[i].sys.updatedAt,
                };
              }
            }
            // multiple entries for contentful query - but no results with questions as a key
            console.error(
              "Multiple items returned from contentful pricing form query but no items returned contained questions array for query: ",
              initial,
              options
            );
          }
        }
      });
    },
    identify() {
      let { email, assetcount, assets, features, company } = this.formFields;

      analyticsAlias(email);
      analyticsIdentify(email, {
        email,
        asset_count: assetcount,
        assets,
        features,
        company,
      });
      analyticsTrack("Form Submitted", {
        form_type: "paid pricing",
        form_url: window.location.pathname,
        is_mobile: isMobile(),
        locale_site: LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()],
        field_data: {
          asset_type: assets.toString(),
          fleet_type: "",
          size: assetcount,
          industry: "",
          feature: features.toString(),
        },
      });
    },
    startCalendarExperiment() {
      const { email, assetcount } = this.formFields;

      // run on paid only
      const isPaid = new URL(location).href.search("page") !== -1;
      if (
        !this.hasUserCompletedExperiment() &&
        assetcount !== "1 - 2" &&
        this.isAfterHours() &&
        this.isUSOrCanada() &&
        isPaid
      ) {
        window.localStorage.setItem("calendarExperiment", email);
        this.setExperimentRedirect();
        analyticsTrack("Calendar Experiment Started");
      }
    },
    hasUserCompletedExperiment() {
      let calendarExperimentItem =
        window.localStorage.getItem("calendarExperiment");

      return calendarExperimentItem;
    },
    setExperimentRedirect() {
      this.$store.commit("thankYou", "book-a-time");
    },
    convertTZ(date, tzString = "UTC") {
      return new Date(
        (typeof date === "string" ? new Date(date) : date).toLocaleString(
          "en-US",
          { timeZone: tzString }
        )
      );
    },
    isDST(date) {
      let jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
      let jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
      return Math.max(jan, jul) !== date.getTimezoneOffset();
    },
    isAfterHours() {
      let selectedLocalTime = new Date();
      let UTCTime = this.convertTZ(selectedLocalTime);
      let UTCHour = UTCTime.getHours();
      let UTCMinutes = UTCTime.getMinutes();
      let Sunday = 0;
      let Saturday = 6;
      let dayOfWeek = selectedLocalTime.getDay();
      let isDST = this.isDST(selectedLocalTime);

      let four_pm_pt = isDST ? 23 : 0;
      let five_pm_pt = isDST ? 0 : 1;
      let three_am_pt = isDST ? 10 : 11;

      if (
        (UTCHour >= five_pm_pt && UTCHour < three_am_pt) ||
        (UTCHour === four_pm_pt && UTCMinutes >= 30) ||
        dayOfWeek == Saturday ||
        dayOfWeek == Sunday
      ) {
        return true;
      }

      return false;
    },
    isUSOrCanada() {
      return this.locale == "ca" || this.locale == "en-US";
    },
    doFormFieldsExist() {
      if (!this.formFields || !this.formFields["businessunit"]) {
        return false;
      }
      return true;
    },
    currentStepDataExperiment(variant) {
      const businessUnit = this.formFields["businessunit"];

      const DEFAULT_FLOW = "A";
      this.flow = DEFAULT_FLOW;

      if (variant === "B") {
        this.flow = this.getExperimentFlowB(businessUnit);
      } else {
        this.flow = "D";
      }
      const currentFormSteps = this.formSteps.filter(
        (step) => step.flow.includes(this.flow) || step.flow === "Standard"
      );

      return currentFormSteps;
    },
    getExperimentFlowB(businessUnit) {
      const FLEET_FLOW = "A";
      const OTHER_FLOW = "B";
      const KVP_FLOW = "E";

      if (businessUnit.includes("Fleets")) {
        if (
          this.formFields["assetcount"] === "1 - 2" ||
          this.formFields["assetcount"] === "3 - 10"
        ) {
          return KVP_FLOW;
        }
        return FLEET_FLOW;
      }
      return OTHER_FLOW;
    },
  },
  async mounted() {
    this.geoLocate();
    this.setReferralID();
        this.trackFunnelStep(
        'paid pricing',
        window.location.pathname,
        LANG_CODE_TO_COUNTRY[this.locale.toLowerCase()],
        this.currentStep,
        null,
        null,
        this.maxSteps,
        this.ipAddress,
        this.flow,
        this.formTitle,
        this.getSize,
        null,
        this.getFleetTypes, 
        this.getIndustryValues,
        this.getFeatureValues
        );
  },
};
</script>

<style lang="scss">
.formstep-title {
  font-family: Roboto;
  font-size: 32px;
  width: 100%;
  max-width: 670px;

  @media (max-width: 415px) {
    font-size: 24px;
    line-height: 32px;
  }
}

.valid-check {
  width: 25px;
  height: 25px;
  position: absolute;
  right: 15px;
  top: 45px;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='25' height='25' viewBox='0 0 17 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M5.42174 11.9355C5.11727 11.9355 4.812 11.8188 4.57945 11.5863L0.348823 7.35645C-0.116274 6.89135 -0.116274 6.13697 0.348823 5.67187C0.81392 5.20677 1.5683 5.20677 2.0334 5.67187L5.42174 9.06021L14.1331 0.348823C14.5982 -0.116274 15.3526 -0.116274 15.8177 0.348823C16.2828 0.81392 16.2828 1.56751 15.8177 2.0326L6.26403 11.5863C6.03148 11.8188 5.72621 11.9355 5.42174 11.9355Z' fill='%230384FB'/%3e%3c/svg%3e ");
}

.input-container {
  max-width: 588px;
  width: 100%;
  position: relative;
}

.legal-text {
  p {
    font-size: 1.2rem;

    a {
      border-bottom: none;
    }
  }
}

.legal-container {
  @media (min-width: 736px) {
    position: absolute;
    bottom: 10px;
    left: 20px;
  }
}
</style>